import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import isEqual from 'react-fast-compare';

import { IBaseProps, ILocation } from '@rbi-ctg/frontend';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useEffectOnce from 'hooks/use-effect-once';
import useReadyQueue from 'hooks/use-ready-queue';
import { OnAppStateChange, useUpdateOnAppStateChange } from 'hooks/use-update-on-app-state-change';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { useMParticleContext } from 'state/mParticle';
import { getDeviceId } from 'utils/device-id';
import { RBIExpandedPlatform, RBIPlatform, checkPlatform, isNativeIOS } from 'utils/environment';
import {
  GeolocationPermissionStates,
  PermissionState,
  checkIfLocationPermissionIsPreapproved,
  determinePermissionsFromGetCurrentPositionErrorCode,
  getUsersCurrentPosition,
} from 'utils/geolocation';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import noop from 'utils/noop';
import { DeviceType, radarTrackOnce } from 'utils/radar';

import { useAuthContext } from '../auth';

export enum CoordinateTypes {
  USER,
  MANUAL,
}

interface ISetCoordinatesProps {
  newCoords: ILocation;
  coordType: CoordinateTypes;
}

export interface IGeolocationCtx {
  loadCurrentPosition: () => Promise<ILocation | undefined>;
  loadingUserCoordinates: boolean;
  manualAddress: string;
  permission: GeolocationPermissionStates;
  setPermission: (permissionState: GeolocationPermissionStates) => void;
  updateManualCoordinates(address: string, placeId?: string | undefined): void;
  coordinatesAvailable: boolean;
  resetCoordinates: (coordType: CoordinateTypes) => void;
  activeCoordinates: ILocation | null;
}

export const GeolocationContext = createContext<IGeolocationCtx>({
  loadCurrentPosition: noop as any,
  loadingUserCoordinates: false,
  manualAddress: '',
  permission: GeolocationPermissionStates.PROMPT,
  setPermission: noop,
  updateManualCoordinates: noop,
  coordinatesAvailable: false,
  resetCoordinates: noop,
  activeCoordinates: null,
});

export const useGeolocation = () => useContext(GeolocationContext);

export const useGeolocationPermission = () => {
  const ctx = useGeolocation();
  return ctx.permission;
};

export const GeolocationProvider = ({ children }: IBaseProps) => {
  const auth = useAuthContext();
  const { deviceId } = useMParticleContext();
  const enableRadarTracking = useFlag(LaunchDarklyFlag.ENABLE_RADAR_TRACKING);
  const logger = useLogger();
  const { enqueueIfNotDrained, drainQueue } = useReadyQueue();

  const [userCoordinates, setUserCoordinates] = useState<ILocation | null>(null);
  const [loadingUserCoordinates, setLoadingUserCoordinates] = useState(false);
  const [permission, setPermissionState] = useState<GeolocationPermissionStates>(
    GeolocationPermissionStates.PROMPT
  );
  const [manualAddress, setManualAddress] = useState('');
  const [manualCoordinates, setManualCoordinates] = useState<ILocation | null>(null);

  const userId = auth.user?.cognitoId || LocalStorage?.getItem(StorageKeys.USER)?.cognitoId;

  const setPermission = useCallback(
    (permissionState: GeolocationPermissionStates) => {
      if (permissionState !== permission) {
        setPermissionState(permissionState);
      }
    },
    [permission]
  );

  useEffect(() => {
    if (permission !== GeolocationPermissionStates.PROMPT) {
      return;
    }

    checkIfLocationPermissionIsPreapproved()
      .then((permissionState: GeolocationPermissionStates) => {
        setPermission(permissionState);
      })
      .catch(permissionState => {
        /** permissionState can be either 'denied' or 'prompt' state here. */
        setPermission(permissionState);
      });
  }, [permission, setPermission]);

  const trackRadar = enqueueIfNotDrained((location, trackedUserId) => {
    let deviceType: DeviceType = RBIExpandedPlatform.WEB;

    if (checkPlatform(RBIPlatform.APP)) {
      deviceType = isNativeIOS() ? RBIExpandedPlatform.IOS : RBIExpandedPlatform.ANDROID;
    }

    getDeviceId().then(loadedDeviceId =>
      radarTrackOnce(location, trackedUserId, loadedDeviceId, deviceType)
    );
  });

  useEffect(() => {
    if (deviceId) {
      drainQueue();
    }
  }, [drainQueue, deviceId]);

  /**
   * resetCoordinates consolidates coordinates resetting logic
   *  for both user coordinates and manually searched ones
   * resetting manual coordinates also resets the manual
   *  address string to empty which can clear search input
   *  so as not to confuse the user on which coords are being
   *  used
   */
  const resetCoordinates = useCallback((coordType: CoordinateTypes) => {
    switch (coordType) {
      case CoordinateTypes.USER:
        setUserCoordinates(null);
        break;
      case CoordinateTypes.MANUAL:
        setManualAddress('');
        setManualCoordinates(null);
        break;
      default:
    }
  }, []);

  /**
   * setCoordinates consolidates coordinates setting logic
   *  for both user coordinates and manually searched ones
   * only updates if those coordinates aren't already set
   *  to the specified value
   * for user coordinates, we also deal with permission
   *  setting and radar tracking, as needed
   */
  const setCoordinates = useCallback(
    ({ newCoords, coordType }: ISetCoordinatesProps) => {
      switch (coordType) {
        case CoordinateTypes.USER:
          if (!isEqual(userCoordinates, newCoords)) {
            // let's only call radar if we're actually changing the user's coords
            if (enableRadarTracking && userId) {
              trackRadar(newCoords, userId);
            }
            // we were able to get user coords so let's set
            // permission to granted if it isn't already
            if (permission !== GeolocationPermissionStates.GRANTED) {
              setPermission(GeolocationPermissionStates.GRANTED);
            }
            setUserCoordinates(newCoords);
          }
          break;
        case CoordinateTypes.MANUAL:
          if (!isEqual(manualCoordinates, newCoords)) {
            setManualCoordinates(newCoords);
          }
          break;
        default:
      }
    },
    [
      enableRadarTracking,
      manualCoordinates,
      permission,
      setPermission,
      trackRadar,
      userCoordinates,
      userId,
    ]
  );

  /**
   * loadCurrentPosition handles determining a user's current
   *  coordinates (provided they have granted location permission)
   *  and triggers setCoordinates for user coords if some are found
   * if not, deals with error handling and updating of permissions
   */
  const loadCurrentPosition = useCallback(() => {
    setLoadingUserCoordinates(true);
    return getUsersCurrentPosition()
      .then(location => {
        if (!location || !location.lat || !location.lng) {
          return Promise.reject({ code: PermissionState.DENIED });
        }

        setCoordinates({
          newCoords: {
            lat: location.lat,
            lng: location.lng,
          },
          coordType: CoordinateTypes.USER,
        });

        return location;
      })
      .catch(error => {
        logger.error({ error, message: 'Error geolocating user' });
        if (error?.code) {
          setPermission(determinePermissionsFromGetCurrentPositionErrorCode(error.code));
          return undefined;
        }
        throw error;
      })
      .finally(() => {
        setLoadingUserCoordinates(false);
      });
  }, [setCoordinates, setPermission, logger]);

  // check if we should trigger loading of user's current position
  // if permission is changed to granted
  // TODO: should this only fire if permission is granted
  //       AND we don't currently have user coords?
  // ❓: is this even needed now that we have the useEffectOnce
  //     and the useUpdateOnAppStateChange?
  useEffect(() => {
    if (permission === GeolocationPermissionStates.GRANTED) {
      loadCurrentPosition();
    }
    // eslint-disable-next-line
  }, [permission]);

  const checkPermissionsAndUpdate = useCallback(async () => {
    const returnedPermissions = await checkIfLocationPermissionIsPreapproved();
    if (returnedPermissions === GeolocationPermissionStates.GRANTED) {
      // only persist permissions at this point
      // if the user has prev granted location
      // if not, we want to re-prompt later on store locator
      setPermission(returnedPermissions);
      loadCurrentPosition();
    }
  }, [loadCurrentPosition, setPermission]);

  const maybeGetLocationAndUpdate: OnAppStateChange = useCallback(
    async ({ isActive }) => {
      if (isActive) {
        checkPermissionsAndUpdate();
      }
    },
    [checkPermissionsAndUpdate]
  );

  useEffectOnce(() => {
    checkPermissionsAndUpdate();
  });

  // get user's current location permissions when app becomes active
  useUpdateOnAppStateChange(maybeGetLocationAndUpdate);

  /**
   * getDetails and updateManualCoordinates deal with manual address search
   *  when invoked, handles empty address passed by triggering
   *    resetCoordinates
   *  otherwise, sets manual address and, if valid placeId is passed,
   *  handles triggering setCoordinates for manual coordinates update
   */
  const getDetails = usePlaceIdDetails();
  const updateManualCoordinates = useCallback(
    (address: string, placeId?: string) => {
      // handle no manual address selected
      if (!address) {
        setManualAddress('');
        resetCoordinates(CoordinateTypes.MANUAL);
      }
      setManualAddress(address);
      getDetails(placeId).then(results => {
        if (!results) {
          return;
        }
        const { geometry } = results;
        const location = {
          lat: geometry!.location.lat(),
          lng: geometry!.location.lng(),
        };

        setCoordinates({ newCoords: location, coordType: CoordinateTypes.MANUAL });
      });
    },
    [getDetails, resetCoordinates, setCoordinates]
  );

  /**
   * activeCoordinates
   *  - prioritizes manual coordinates > user coordinates
   *  - only updates if current coordinates don't match
   */
  const activeCoordinates = useMemo(() => {
    if (manualCoordinates) {
      return manualCoordinates;
    }
    if (userCoordinates) {
      return userCoordinates;
    }
    return null;
  }, [manualCoordinates, userCoordinates]);

  // enable quick check for whether or not we have coords
  const coordinatesAvailable = !!activeCoordinates;

  return (
    <GeolocationContext.Provider
      value={{
        permission,
        loadCurrentPosition,
        loadingUserCoordinates,
        setPermission,
        updateManualCoordinates,
        manualAddress,
        coordinatesAvailable,
        resetCoordinates,
        activeCoordinates,
      }}
    >
      {children}
    </GeolocationContext.Provider>
  );
};
